/**
 * FP dashboard → Resources tab curated panel (Feature 096).
 * "bQuest Recommended Resources" below the My Resources card — the global
 * recommended list (Feature 086 predicate, served by the resources API) with a
 * one-click Share action into the dashboard's existing ShareDialog.
 */

import { Link } from 'react-router-dom';
import { BookOpen, Share2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { CuratedSection } from '@/shared/components/curated';
import { useResources } from '@/features/resource-hub/hooks/useResources';
import type { FeaturedResource } from '../api/resourcesApi';

const SUBTITLE = 'Share any of these with a client in one click.';
const LIMIT = 6;

const FEATURED_TYPES = ['ARTICLE', 'VIDEO', 'PODCAST', 'GUIDE'] as const;

/** ShareDialog's FeaturedResource union is narrower than resource-hub type
 *  names — anything outside it ("Checklist", …) falls back to ARTICLE. */
const toFeaturedType = (typeName: string): FeaturedResource['type'] => {
  const upper = typeName.toUpperCase();
  return (FEATURED_TYPES as readonly string[]).includes(upper)
    ? (upper as FeaturedResource['type'])
    : 'ARTICLE';
};

type CuratedResourcesCardProps = {
  onShareClick: (resource: FeaturedResource) => void;
};

export function CuratedResourcesCard({ onShareClick }: CuratedResourcesCardProps) {
  const { data, isLoading, isError } = useResources({
    recommended: true,
    limit: LIMIT,
    sort: 'newest',
  });

  if (isLoading) {
    return (
      <div className="mt-8 space-y-3" data-testid="curated-resources-card-skeleton">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  const resources = data?.data ?? [];
  if (isError || resources.length === 0) {
    return null;
  }

  return (
    <div data-testid="curated-resources-card">
      <CuratedSection title="bQuest Recommended Resources" subtitle={SUBTITLE}>
        <div className="rounded-lg bg-white border divide-y">
          {resources.map((resource) => (
            <div key={resource.id} className="flex items-center gap-3 p-3">
              <div className="w-10 h-10 rounded bg-[#026857]/10 flex items-center justify-center flex-shrink-0">
                <BookOpen className="w-5 h-5 text-[#026857]" />
              </div>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/resource-hub-detail/${encodeURIComponent(resource.seoUrl)}`}
                  className="text-sm font-medium text-gray-900 hover:underline block truncate"
                >
                  {resource.title}
                </Link>
                <p className="text-xs text-gray-500 truncate">
                  <span className="font-semibold uppercase tracking-wide text-[#026857]">
                    {resource.type.name}
                  </span>
                  {resource.categories[0] && <> · {resource.categories[0].name}</>}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10 flex-shrink-0"
                title="Share resource with a client"
                onClick={() =>
                  onShareClick({
                    id: resource.id,
                    title: resource.title,
                    description: resource.excerpt,
                    type: toFeaturedType(resource.type.name),
                    category: resource.categories[0]?.name ?? '',
                    thumbnailUrl: null,
                    url: `/resource-hub-detail/${encodeURIComponent(resource.seoUrl)}`,
                  })
                }
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          ))}
        </div>
      </CuratedSection>
    </div>
  );
}
